import { useCallback, useEffect, useRef, useState } from "react";
import deepgramService from "../services/deepgramService";
import { DeepgramLanguage } from "../constants/deepgramLanguages";
import {
  buildDeepgramListenConfig,
  CLOSE_STREAM_WAIT_MS,
  DEEPGRAM_PLAYGROUND_COMPAT_MODE,
  MEDIA_RECORDER_TIMESLICE_MS,
  RECENT_SEGMENT_DEDUPE_SIZE,
  sanitizeDeepgramWsUrl,
  USE_PCM_STREAMING,
  wsReadyStateLabel,
} from "../constants/deepgramConfig";
import {
  RecordingResult,
  RecordingStatus,
  TranscriptSegment,
} from "../types/transcript";

const KEEPALIVE_MS = 8000;

// Mic-activity detection. Two thresholds plus a hold window stop the indicator
// flickering on the natural pauses in speech: it latches on above ON, and only
// clears after staying below OFF for the whole hold window.
const MIC_DETECT_ON_LEVEL = 0.02;
// Visualiser: one value per bar, taken from the spectrum.
const MIC_BAND_COUNT = 40;
const MIC_BAND_DECAY = 0.82;          // fall speed; rises are instant
const MIC_PUBLISH_INTERVAL_MS = 33;   // ~30fps into React
const MIC_DETECT_OFF_LEVEL = 0.008;
const MIC_SILENCE_HOLD_MS = 2000;

// Mid-recording WebSocket recovery. A drop can be a transient network blip or
// an expired Deepgram token — both are handled by re-minting a fresh token and
// reconnecting. Audio keeps buffering locally during the gap (only the live
// transcript for that window is lost; the backend re-transcribes the audio).
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 500;
const RECONNECT_MAX_DELAY_MS = 8000;
const IS_DEV = process.env.NODE_ENV === "development";

const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
    channelCount: 1,
  },
};

type WsAuthScheme = "bearer" | "token";

const LIVE_STATUSES: RecordingStatus[] = [
  "requesting_mic",
  "connecting",
  "recording",
  "paused",
  "stopping",
];

export interface RecordingDebugInfo {
  languageLabel: string;
  languageCode: string;
  deepgramConfig: ReturnType<typeof buildDeepgramListenConfig>;
  wsUrlSanitized: string;
  wsStatus: string;
  mediaRecorderMimeType: string;
  mediaRecorderState: string;
  micTrackSettings: MediaTrackSettings | null;
  chunkCount: number;
  totalBytesSent: number;
  averageChunkSize: number;
  firstChunkSizes: number[];
  transcriptSegmentCount: number;
  transcriptCharCount: number;
  lastDeepgramMessageType: string;
  lastTranscriptText: string;
}

interface ParsedDeepgramMessage {
  messageType: string;
  transcript: string;
  isFinal: boolean;
}

function debugLog(...args: unknown[]) {
  if (IS_DEV) console.log("[DeepgramRecorder]", ...args);
}

function normalizeForDedupe(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, " ");
}

// Exported for unit tests.
export function isDuplicateSegment(text: string, recent: string[]): boolean {
  const norm = normalizeForDedupe(text);
  if (!norm) return true;
  return recent.some((r) => normalizeForDedupe(r) === norm);
}

function parseDeepgramMessage(raw: string): ParsedDeepgramMessage | null {
  try {
    const msg = JSON.parse(raw) as {
      type?: string;
      channel?: { alternatives?: Array<{ transcript?: string }> };
      is_final?: boolean;
      speech_final?: boolean;
      message?: string;
      description?: string;
    };

    if (msg.type === "Error") {
      const detail =
        msg.message || msg.description || "Deepgram rejected the request";
      throw new Error(`Deepgram error: ${detail}`);
    }

    if (msg.type === "Metadata" || msg.type === "SpeechStarted") {
      return { messageType: msg.type || "unknown", transcript: "", isFinal: false };
    }

    const transcript = msg.channel?.alternatives?.[0]?.transcript?.trim() || "";
    if (!transcript) {
      return {
        messageType: msg.type || "Results",
        transcript: "",
        isFinal: Boolean(msg.is_final || msg.speech_final),
      };
    }

    const isFinal = DEEPGRAM_PLAYGROUND_COMPAT_MODE
      ? true
      : Boolean(msg.is_final || msg.speech_final);

    return {
      messageType: msg.type || "Results",
      transcript,
      isFinal,
    };
  } catch (e) {
    if (e instanceof Error && e.message.startsWith("Deepgram error:")) {
      throw e;
    }
    return null;
  }
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function logWsClose(scheme: WsAuthScheme, event: CloseEvent) {
  console.error("Deepgram WebSocket closed", {
    scheme,
    code: event.code,
    reason: event.reason,
    wasClean: event.wasClean,
  });
}

function openDeepgramWebSocket(
  wsUrl: string,
  accessToken: string,
  scheme: WsAuthScheme,
  onStatus: (status: string) => void
): Promise<WebSocket> {
  return new Promise((resolve, reject) => {
    let settled = false;
    onStatus("CONNECTING");
    debugLog(`WebSocket connecting (scheme=${scheme})`, {
      ws_url: sanitizeDeepgramWsUrl(wsUrl),
    });

    const ws = new WebSocket(wsUrl, [scheme, accessToken]);

    ws.onopen = () => {
      settled = true;
      onStatus("OPEN");
      debugLog(`WebSocket onopen (scheme=${scheme})`);
      resolve(ws);
    };

    ws.onerror = () => {
      onStatus("ERROR");
      console.error(`Deepgram WebSocket onerror (scheme=${scheme})`);
    };

    ws.onclose = (event) => {
      onStatus("CLOSED");
      logWsClose(scheme, event);
      if (!settled) {
        settled = true;
        const detail = event.reason
          ? `${event.code}: ${event.reason}`
          : String(event.code);
        reject(
          new Error(`WebSocket closed before opening [${scheme}] (${detail})`)
        );
      }
    };
  });
}

async function connectDeepgramWithFallback(
  wsUrl: string,
  accessToken: string,
  onStatus: (status: string) => void
): Promise<WebSocket> {
  try {
    return await openDeepgramWebSocket(wsUrl, accessToken, "bearer", onStatus);
  } catch (bearerErr) {
    const msg =
      bearerErr instanceof Error ? bearerErr.message : String(bearerErr);
    console.warn(
      "Deepgram bearer subprotocol failed, retrying with token subprotocol",
      msg
    );
    return openDeepgramWebSocket(wsUrl, accessToken, "token", onStatus);
  }
}

function chooseMediaRecorderMimeType(): string {
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
    return "audio/webm;codecs=opus";
  }
  if (MediaRecorder.isTypeSupported("audio/webm")) {
    return "audio/webm";
  }
  return "";
}

export interface UseDeepgramRecorderOptions {
  // Sent with the token request purely for the HIPAA recording-start audit entry.
  patientId?: number | null;
  transcriptionLanguage: DeepgramLanguage;
  // Called once the recording is stopped and its audio blob + final transcript
  // have been assembled. The caller reads getRecordingResult() to POST to the
  // SOAP `/generate/` pipeline.
  onFinalized?: (result: RecordingResult) => void;
}

const EMPTY_DEBUG: RecordingDebugInfo = {
  languageLabel: "",
  languageCode: "",
  deepgramConfig: buildDeepgramListenConfig("multi"),
  wsUrlSanitized: "",
  wsStatus: "NONE",
  mediaRecorderMimeType: "",
  mediaRecorderState: "inactive",
  micTrackSettings: null,
  chunkCount: 0,
  totalBytesSent: 0,
  averageChunkSize: 0,
  firstChunkSizes: [],
  transcriptSegmentCount: 0,
  transcriptCharCount: 0,
  lastDeepgramMessageType: "",
  lastTranscriptText: "",
};

export function useDeepgramRecorder({
  patientId,
  transcriptionLanguage,
  onFinalized,
}: UseDeepgramRecorderOptions) {
  const [status, setStatus] = useState<RecordingStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [interimText, setInterimText] = useState("");
  const [finalSegments, setFinalSegments] = useState<TranscriptSegment[]>([]);
  const [savedDisplayText, setSavedDisplayText] = useState("");
  const [debugInfo, setDebugInfo] = useState<RecordingDebugInfo>(EMPTY_DEBUG);
  const [micLevel, setMicLevel] = useState(0);
  const [micInputDetected, setMicInputDetected] = useState(false);
  const [micBands, setMicBands] = useState<number[]>(() =>
    new Array(MIC_BAND_COUNT).fill(0),
  );

  const wsRef = useRef<WebSocket | null>(null);
  const wsReadyRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const keepAliveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingActiveRef = useRef(false);
  const segmentsRef = useRef<TranscriptSegment[]>([]);
  const recordingAttemptIdRef = useRef(0);
  const startingRef = useRef(false);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioMimeRef = useRef<string>("audio/webm");
  const resultRef = useRef<RecordingResult | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds the latest connect fn so onclose can trigger a reconnect without a
  // circular useCallback dependency.
  const connectWsRef = useRef<
    ((attemptId: number) => Promise<void>) | null
  >(null);
  const chunkCountRef = useRef(0);
  const totalBytesSentRef = useRef(0);
  const firstChunkSizesRef = useRef<number[]>([]);
  const lastMessageTypeRef = useRef("");
  const lastTranscriptRef = useRef("");
  const wsStatusRef = useRef("NONE");
  const wsUrlSanitizedRef = useRef("");
  const micTrackSettingsRef = useRef<MediaTrackSettings | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const levelRafRef = useRef<number | null>(null);

  const refreshDebugInfo = useCallback(() => {
    const expectedConfig = buildDeepgramListenConfig(transcriptionLanguage.code);
    const segmentCount = segmentsRef.current.length;
    const charCount = segmentsRef.current.map((s) => s.text).join(" ").length;
    const chunkCount = chunkCountRef.current;
    const totalBytes = totalBytesSentRef.current;

    setDebugInfo({
      languageLabel: transcriptionLanguage.label,
      languageCode: transcriptionLanguage.code,
      deepgramConfig: expectedConfig,
      wsUrlSanitized: wsUrlSanitizedRef.current,
      wsStatus: wsStatusRef.current,
      mediaRecorderMimeType: audioMimeRef.current,
      mediaRecorderState: mediaRecorderRef.current?.state ?? "inactive",
      micTrackSettings: micTrackSettingsRef.current,
      chunkCount,
      totalBytesSent: totalBytes,
      averageChunkSize: chunkCount > 0 ? Math.round(totalBytes / chunkCount) : 0,
      firstChunkSizes: [...firstChunkSizesRef.current],
      transcriptSegmentCount: segmentCount,
      transcriptCharCount: charCount,
      lastDeepgramMessageType: lastMessageTypeRef.current,
      lastTranscriptText: lastTranscriptRef.current,
    });
  }, [transcriptionLanguage]);

  const stopMicLevelMonitor = useCallback(() => {
    if (levelRafRef.current !== null) {
      cancelAnimationFrame(levelRafRef.current);
      levelRafRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    setMicLevel(0);
    setMicInputDetected(false);
    setMicBands(new Array(MIC_BAND_COUNT).fill(0));
  }, []);

  const startMicLevelMonitor = useCallback(
    (stream: MediaStream) => {
      stopMicLevelMonitor();
      try {
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        audioContextRef.current = audioContext;

        const timeData = new Uint8Array(analyser.fftSize);
        const freqData = new Uint8Array(analyser.frequencyBinCount);
        // Smoothed per-bar values. The visualiser needs one value *per bar* from
        // the spectrum: driving every bar from a single RMS scalar (as before)
        // produced a shape that never changed, only scaled — so it read as
        // static dots rather than something reacting to the voice.
        const bands = new Array(MIC_BAND_COUNT).fill(0);

        // The raw level crosses any single threshold many times a second — every
        // pause between words dips to silence — so comparing it directly made
        // the indicator strobe. Speech is detected on a higher threshold and
        // only cleared after a sustained quiet period below a lower one
        // (hysteresis + hold), so the badge reports "no input" for a genuinely
        // dead mic rather than for the gaps in normal speech.
        let detected = false;
        let lastLoudAt = performance.now();
        let lastPublish = 0;

        const tick = () => {
          analyser.getByteTimeDomainData(timeData);
          let sum = 0;
          for (let i = 0; i < timeData.length; i++) {
            const v = (timeData[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / timeData.length);
          const level = Math.min(1, rms * 5);

          // Speech energy sits in the low bins, so sample only the lower part of
          // the spectrum — the top bins are near-silent and would render as a
          // permanently flat tail.
          analyser.getByteFrequencyData(freqData);
          const usableBins = Math.floor(freqData.length * 0.6);
          const perBand = Math.max(1, Math.floor(usableBins / MIC_BAND_COUNT));
          for (let b = 0; b < MIC_BAND_COUNT; b++) {
            let peak = 0;
            const start = b * perBand;
            for (let i = start; i < start + perBand && i < usableBins; i++) {
              if (freqData[i] > peak) peak = freqData[i];
            }
            // sqrt keeps quiet speech visible: the raw byte value is linear, and
            // conversational levels sit low enough that a linear map collapsed
            // every bar onto the minimum height.
            const target = Math.sqrt(peak / 255);
            // Rise fast, fall slowly, so bars track speech instead of flickering.
            bands[b] =
              target > bands[b]
                ? target
                : bands[b] * MIC_BAND_DECAY + target * (1 - MIC_BAND_DECAY);
          }

          const now = performance.now();
          if (level > MIC_DETECT_ON_LEVEL) {
            lastLoudAt = now;
            if (!detected) {
              detected = true;
              setMicInputDetected(true);
            }
          } else if (
            detected &&
            level < MIC_DETECT_OFF_LEVEL &&
            now - lastLoudAt > MIC_SILENCE_HOLD_MS
          ) {
            detected = false;
            setMicInputDetected(false);
          }

          // Publish to React at ~30fps rather than every frame: 60 state updates
          // a second re-render the whole page for no visible gain.
          if (now - lastPublish >= MIC_PUBLISH_INTERVAL_MS) {
            lastPublish = now;
            setMicLevel(level);
            setMicBands([...bands]);
          }

          levelRafRef.current = requestAnimationFrame(tick);
        };
        levelRafRef.current = requestAnimationFrame(tick);
      } catch (err) {
        console.warn("Mic level monitor unavailable", err);
      }
    },
    [stopMicLevelMonitor]
  );

  const stopKeepAlive = useCallback(() => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
  }, []);

  const startKeepAlive = useCallback(() => {
    stopKeepAlive();
    keepAliveRef.current = setInterval(() => {
      const ws = wsRef.current;
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "KeepAlive" }));
      }
    }, KEEPALIVE_MS);
  }, [stopKeepAlive]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(() => {
      if (recordingActiveRef.current) {
        setElapsedSec((n) => n + 1);
      }
    }, 1000);
  }, [stopTimer]);

  const cleanupMedia = useCallback(() => {
    stopMicLevelMonitor();
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    mediaRecorderRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    micTrackSettingsRef.current = null;
  }, [stopMicLevelMonitor]);

  const cleanupWs = useCallback(() => {
    stopKeepAlive();
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    wsReadyRef.current = false;
    wsStatusRef.current = "CLOSED";
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: "CloseStream" }));
        } catch {
          /* ignore */
        }
      }
      ws.close();
    }
  }, [stopKeepAlive]);

  const resetTranscriptForNewRecording = useCallback(() => {
    segmentsRef.current = [];
    audioChunksRef.current = [];
    chunkCountRef.current = 0;
    totalBytesSentRef.current = 0;
    firstChunkSizesRef.current = [];
    lastMessageTypeRef.current = "";
    lastTranscriptRef.current = "";
    setFinalSegments([]);
    setInterimText("");
    setSavedDisplayText("");
    setError(null);
    setElapsedSec(0);
  }, []);

  const appendTranscriptSegment = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const recent = segmentsRef.current
        .slice(-RECENT_SEGMENT_DEDUPE_SIZE)
        .map((s) => s.text);
      if (isDuplicateSegment(trimmed, recent)) {
        debugLog("Skipped duplicate segment", trimmed);
        return;
      }

      const seg: TranscriptSegment = { text: trimmed, is_final: true };
      segmentsRef.current = [...segmentsRef.current, seg];
      setFinalSegments(segmentsRef.current);
      lastTranscriptRef.current = trimmed;
      refreshDebugInfo();
    },
    [refreshDebugInfo]
  );

  // Assemble the recorded audio into a single Blob (no upload here — the
  // caller POSTs it to the SOAP /generate/ pipeline).
  const assembleAudioBlob = useCallback((): Blob | null => {
    const chunks = audioChunksRef.current;
    if (!chunks.length) return null;
    const blob = new Blob(chunks, { type: audioMimeRef.current });
    return blob.size === 0 ? null : blob;
  }, []);

  useEffect(() => {
    return () => {
      recordingActiveRef.current = false;
      startingRef.current = false;
      stopTimer();
      cleanupMedia();
      cleanupWs();
    };
  }, [cleanupMedia, cleanupWs, stopTimer]);

  const finalTranscriptText = finalSegments.map((s) => s.text).join(" ").trim();

  const liveDisplayText = DEEPGRAM_PLAYGROUND_COMPAT_MODE
    ? finalTranscriptText
    : [finalTranscriptText, interimText].filter(Boolean).join(" ").trim();

  const displayTranscriptText = LIVE_STATUSES.includes(status)
    ? liveDisplayText
    : savedDisplayText || finalTranscriptText;

  // Finalize a stopped recording: assemble the audio blob + final transcript
  // and hand them to the caller (no backend persistence here — the caller POSTs
  // to the SOAP /generate/ pipeline).
  const finalizeRecording = useCallback(async () => {
    const segments = segmentsRef.current;
    const transcript_text = segments
      .map((s) => s.text)
      .join(" ")
      .trim();
    const audioBlob = assembleAudioBlob();
    const result: RecordingResult = {
      audioBlob,
      mimeType: audioMimeRef.current || "audio/webm",
      transcriptText: transcript_text,
      segments,
      languageCode: transcriptionLanguage.code,
      languageLabel: transcriptionLanguage.label,
    };
    resultRef.current = result;
    setInterimText("");
    setSavedDisplayText(transcript_text);
    setStatus("stopped");
    onFinalized?.(result);
  }, [assembleAudioBlob, onFinalized, transcriptionLanguage]);

  const handleStop = useCallback(async () => {
    if (status !== "recording" && status !== "paused") return;
    setStatus("stopping");
    recordingActiveRef.current = false;

    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    }

    stopTimer();

    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "CloseStream" }));
      } catch {
        /* ignore */
      }
      debugLog(`Waiting ${CLOSE_STREAM_WAIT_MS}ms for final Deepgram messages`);
      await new Promise((r) => setTimeout(r, CLOSE_STREAM_WAIT_MS));
    }

    cleanupMedia();
    cleanupWs();
    setInterimText("");
    refreshDebugInfo();

    try {
      await finalizeRecording();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to finalize recording");
      setStatus("error");
    }
  }, [
    status,
    stopTimer,
    cleanupMedia,
    cleanupWs,
    finalizeRecording,
    refreshDebugInfo,
  ]);

  const handleDeepgramMessage = useCallback(
    (raw: string, attemptId: number) => {
      if (recordingAttemptIdRef.current !== attemptId) return;

      const parsed = parseDeepgramMessage(raw);
      if (!parsed) return;

      lastMessageTypeRef.current = parsed.messageType;
      debugLog("Deepgram message", {
        type: parsed.messageType,
        transcript: parsed.transcript || "(empty)",
        isFinal: parsed.isFinal,
      });

      if (!parsed.transcript) return;

      if (DEEPGRAM_PLAYGROUND_COMPAT_MODE || parsed.isFinal) {
        appendTranscriptSegment(parsed.transcript);
        if (!DEEPGRAM_PLAYGROUND_COMPAT_MODE) {
          setInterimText("");
        }
      } else {
        setInterimText(parsed.transcript);
      }
    },
    [appendTranscriptSegment]
  );

  // Mint a fresh Deepgram token, open the WebSocket and wire its handlers.
  // Used for the initial connect and for every mid-recording reconnect — the
  // token is re-minted each time, so an expired token self-heals.
  const connectWs = useCallback(
    async (attemptId: number) => {
      const expectedConfig = buildDeepgramListenConfig(
        transcriptionLanguage.code
      );

      const tokenRes = await deepgramService.getDeepgramToken(
        transcriptionLanguage.code,
        patientId
      );
      if (recordingAttemptIdRef.current !== attemptId) return;

      const sanitizedUrl = sanitizeDeepgramWsUrl(tokenRes.ws_url);
      wsUrlSanitizedRef.current = sanitizedUrl;
      setDebugInfo((prev) => ({ ...prev, wsUrlSanitized: sanitizedUrl }));

      debugLog("Token received", {
        expires_in: tokenRes.expires_in,
        ws_url: sanitizedUrl,
        protocol_scheme: tokenRes.ws_protocols?.[0] ?? "bearer",
        deepgram_config: tokenRes.deepgram_config ?? expectedConfig,
      });

      wsReadyRef.current = false;
      const ws = await connectDeepgramWithFallback(
        tokenRes.ws_url,
        tokenRes.access_token,
        (s) => {
          wsStatusRef.current = s;
          refreshDebugInfo();
        }
      );
      if (recordingAttemptIdRef.current !== attemptId) {
        ws.close();
        return;
      }
      wsRef.current = ws;
      wsReadyRef.current = true;
      wsStatusRef.current = wsReadyStateLabel(ws.readyState);
      reconnectAttemptsRef.current = 0;

      ws.onmessage = (event) => {
        if (typeof event.data !== "string") return;
        try {
          handleDeepgramMessage(event.data, attemptId);
        } catch (parseErr) {
          const msg =
            parseErr instanceof Error
              ? parseErr.message
              : "Deepgram transcription error";
          console.error(msg);
          setError(msg);
          setStatus("error");
          recordingActiveRef.current = false;
          stopTimer();
          cleanupMedia();
          cleanupWs();
        }
      };

      ws.onclose = (event) => {
        wsStatusRef.current = "CLOSED";
        wsReadyRef.current = false;
        logWsClose("bearer", event);
        refreshDebugInfo();

        // Only recover from drops that happen while we are still recording —
        // a deliberate stop clears recordingActiveRef before closing.
        if (
          !recordingActiveRef.current ||
          recordingAttemptIdRef.current !== attemptId
        ) {
          return;
        }

        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          setError(
            "Live transcription disconnected. The audio is still being recorded — stop to generate the note."
          );
          return;
        }

        const delay = Math.min(
          RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttemptsRef.current,
          RECONNECT_MAX_DELAY_MS
        );
        reconnectAttemptsRef.current += 1;
        debugLog("Scheduling Deepgram reconnect", {
          attempt: reconnectAttemptsRef.current,
          delay,
        });

        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(() => {
          if (
            !recordingActiveRef.current ||
            recordingAttemptIdRef.current !== attemptId
          ) {
            return;
          }
          connectWsRef.current?.(attemptId).catch((err) => {
            console.warn("Deepgram reconnect failed", err);
            // onclose from the failed attempt schedules the next retry; if the
            // socket never opened, retry directly.
            if (
              recordingActiveRef.current &&
              recordingAttemptIdRef.current === attemptId &&
              reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS
            ) {
              ws.onclose?.(event);
            }
          });
        }, delay);
      };

      startKeepAlive();
    },
    [
      patientId,
      transcriptionLanguage,
      handleDeepgramMessage,
      refreshDebugInfo,
      startKeepAlive,
      stopTimer,
      cleanupMedia,
      cleanupWs,
    ]
  );

  useEffect(() => {
    connectWsRef.current = connectWs;
  }, [connectWs]);

  const startRecording = useCallback(async () => {
    if (startingRef.current || status === "stopping") return;
    if (USE_PCM_STREAMING) {
      setError("PCM streaming mode is not implemented yet.");
      return;
    }

    startingRef.current = true;
    recordingAttemptIdRef.current += 1;
    const attemptId = recordingAttemptIdRef.current;

    resetTranscriptForNewRecording();
    cleanupMedia();
    cleanupWs();

    const expectedConfig = buildDeepgramListenConfig(transcriptionLanguage.code);
    setDebugInfo({
      ...EMPTY_DEBUG,
      languageLabel: transcriptionLanguage.label,
      languageCode: transcriptionLanguage.code,
      deepgramConfig: expectedConfig,
    });

    setStatus("requesting_mic");
    try {
      const stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
      if (recordingAttemptIdRef.current !== attemptId) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;

      const track = stream.getAudioTracks()[0];
      const settings = track?.getSettings() ?? null;
      micTrackSettingsRef.current = settings;
      debugLog("Mic track settings", settings);
      console.log("Mic track settings", settings);

      startMicLevelMonitor(stream);
      setStatus("connecting");

      debugLog("Token request", {
        language: transcriptionLanguage.code,
        config: expectedConfig,
      });

      const mimeType = chooseMediaRecorderMimeType();
      debugLog("MediaRecorder MIME type", mimeType || "(browser default)");
      audioMimeRef.current = mimeType || "audio/webm";

      reconnectAttemptsRef.current = 0;
      await connectWs(attemptId);
      if (recordingAttemptIdRef.current !== attemptId) return;
      if (!wsRef.current) throw new Error("Could not open transcription stream");

      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (ev) => {
        if (recordingAttemptIdRef.current !== attemptId) return;
        if (!ev.data || ev.data.size === 0) return;

        audioChunksRef.current.push(ev.data);

        if (!wsReadyRef.current) return;
        if (!recordingActiveRef.current) return;

        const activeWs = wsRef.current;
        if (!activeWs || activeWs.readyState !== WebSocket.OPEN) return;

        chunkCountRef.current += 1;
        totalBytesSentRef.current += ev.data.size;
        firstChunkSizesRef.current = [
          ...firstChunkSizesRef.current,
          ev.data.size,
        ].slice(0, 20);
        activeWs.send(ev.data);
        refreshDebugInfo();
      };

      // Start recorder only after WebSocket is open.
      recorder.start(MEDIA_RECORDER_TIMESLICE_MS);
      recordingActiveRef.current = true;
      startTimer();
      setStatus("recording");
      refreshDebugInfo();
    } catch (e) {
      if (recordingAttemptIdRef.current === attemptId) {
        cleanupMedia();
        cleanupWs();
        setError(e instanceof Error ? e.message : "Could not start recording");
        setStatus("error");
      }
    } finally {
      startingRef.current = false;
    }
  }, [
    status,
    transcriptionLanguage,
    connectWs,
    startTimer,
    cleanupMedia,
    cleanupWs,
    resetTranscriptForNewRecording,
    startMicLevelMonitor,
    refreshDebugInfo,
  ]);

  const pauseRecording = useCallback(() => {
    if (status !== "recording") return;
    const rec = mediaRecorderRef.current;
    if (rec?.state === "recording") rec.pause();
    recordingActiveRef.current = false;
    setStatus("paused");
    refreshDebugInfo();
  }, [status, refreshDebugInfo]);

  const resumeRecording = useCallback(() => {
    if (status !== "paused") return;
    const rec = mediaRecorderRef.current;
    if (rec?.state === "paused") rec.resume();
    recordingActiveRef.current = true;
    setStatus("recording");
    refreshDebugInfo();
  }, [status, refreshDebugInfo]);

  const isStoppingOrSaving = status === "stopping";

  return {
    status,
    error,
    elapsedLabel: formatElapsed(elapsedSec),
    displayTranscriptText,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording: handleStop,
    getRecordingResult: () => resultRef.current,
    isRecording: status === "recording",
    isPaused: status === "paused",
    isStoppingOrSaving,
    canStart:
      (status === "idle" || status === "error" || status === "stopped") &&
      !startingRef.current,
    canPause: status === "recording",
    canResume: status === "paused" && !isStoppingOrSaving,
    canStop:
      status === "recording" || status === "paused" || status === "connecting",
    debugInfo,
    micLevel,
    micBands,
    micInputDetected,
  };
};
